import { Breadcrumbs, Divider, Group, IconFont } from '@byecode/ui'
import { BACKGROUND_TYPE, type FieldInputType, FILE_MODE } from '@lighthouse/core'
import { FieldInputNameMap, getFieldOptions, getPrimaryDataSourceEnableFieldIds } from '@lighthouse/shared'
import React, { useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { AnchorSetting } from '@/components/AnchorSetting'
import type { DesignColorSetting } from '@/components/StyleSetting'
import {
    DesignSetting,
    FontSetting,
    getBlockAndPageDesignLimit,
    getFieldBlockInteractionStyleLimit,
    getFieldBlockStyleLimit
} from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { InteractionSetting } from '@/components/StyleSetting/InteractionSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'

import type { BlockSettingsSegmentedEnum } from '../../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../../constants'
import { BlockConfigureSegmentedControl } from '../BlockConfigureSegmentedControl'
import { CursorSetting } from '../CursorSetting'
import { EVENTS_FIELDS, relativeSelectFieldList } from './constants'
import { Data } from './Data'
import { CheckboxConfig } from './Data/CheckboxConfig'
import { EventsConfigure } from './Events'
import type { FieldInputFormSlice } from './formSlice'
import { Style } from './Style'
import { CalendarMode } from './Style/CalendarMode'
import { FieldExtraStyle } from './Style/FieldExtraStyle'
import { FileGridStyle } from './Style/FileGridStyle'
import { FileMode } from './Style/FileMode'
import type { FieldInputSettingProps } from './types'

const SCxContainer = styled.div``
const SCxItem = styled.div``

const sliderBackgroundSetting: DesignColorSetting = {
    background: [BACKGROUND_TYPE.color],
    color: [BACKGROUND_TYPE.color]
}

export const FieldInputSetting: React.FunctionComponent<FieldInputSettingProps> = ({
    mode,
    dataSourceList,
    prefix = '',
    id,
    pointer,
    style,
    onBack
}) => {
    const isSubForm = mode === 'subForm'
    const isNotSubForm = mode !== 'subForm'
    // const { watch } = useFormContext<FieldInputConfigProtocol | SubFormBlockConfig>()
    const { watch } = useFormContext<FieldInputFormSlice>()
    // const fieldId = watch(`${prefix}fieldPoint`)
    const inputType = watch(`${prefix}inputType`) as FieldInputType
    const theme = watch(`${prefix}breakPoint.design.theme`)
    const showMode = watch(`${prefix}relativeSelect.showMode`)
    const fileMode = watch(`${prefix}file.mode`)
    const fieldPointer = watch(`${prefix}fieldPointer`)
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(
        isSubForm ? BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE : BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()

    const dataSource = useDataSource(appId, envId, pointer)
    const { schema, viewOptions, type } = dataSource || {}
    const { tableProps = [] } = viewOptions || {}
    const fieldOptions = useMemo(() => getFieldOptions(schema, tableProps), [schema, tableProps])

    const primaryDataSourceFieldIds = useMemo(() => {
        if (!schema || !viewOptions || !type) {
            return
        }
        return getPrimaryDataSourceEnableFieldIds(schema, viewOptions, type)
    }, [schema, viewOptions, type])

    const field = useMemo(() => {
        if (primaryDataSourceFieldIds?.has(fieldPointer)) {
            return dataSource?.schema?.[fieldPointer]
        }
    }, [dataSource?.schema, fieldPointer, primaryDataSourceFieldIds])

    const styleProps = getBlockAndPageDesignLimit('field')
    const isSelectField = useMemo(() => (field?.type ? relativeSelectFieldList.has(field.type) : false), [field?.type])

    const designProps = getFieldBlockStyleLimit(inputType, {
        relativeSelectShowMode: showMode,
        isSelectField
    })
    const enableInputFontTextStyle = useMemo(() => ['text', 'number', 'email', 'url', 'phoneNumber'].includes(inputType), [inputType])
    const interactionProps = getFieldBlockInteractionStyleLimit(inputType, {
        relativeSelectShowMode: showMode,
        isSelectField
    })

    const filterFieldOptions = useMemo(
        () =>
            fieldOptions.filter(item => {
                return primaryDataSourceFieldIds?.has(item.value)
            }),
        [fieldOptions, primaryDataSourceFieldIds]
    )

    const designAfterSlot = useMemo(() => {
        if (inputType === 'person') {
            return (
                <>
                    {interactionProps && <InteractionSetting {...interactionProps} />}
                    <FieldExtraStyle prefixName="breakPoint.personDesign" inputType={inputType} isPersonTag />
                </>
            )
        }

        if (inputType === 'slider') {
            return (
                <>
                    {interactionProps && <InteractionSetting {...interactionProps} />}
                    <FieldExtraStyle
                        prefixName="breakPoint.sliderDesign"
                        colorSetting={sliderBackgroundSetting}
                        inputType={inputType}
                        isSlider
                    />
                </>
            )
        }

        if (inputType === 'file') {
            return (
                <>
                    {interactionProps && <InteractionSetting {...interactionProps} />}
                    <FieldExtraStyle prefixName="breakPoint.fileDesign" inputType={inputType} isFileUploaded />
                </>
            )
        }

        if (inputType === 'relativeSelect') {
            return (
                <>
                    {interactionProps && <InteractionSetting {...interactionProps} />}
                    {!isSelectField && showMode === 'input' && (
                        <FieldExtraStyle
                            prefixName="breakPoint.selectDesign"
                            inputType={inputType}
                            relativeSelectShowMode={showMode}
                            isSelectField={isSelectField}
                            isSelectTag
                        />
                    )}
                </>
            )
        }

        return interactionProps && <InteractionSetting {...interactionProps} />
    }, [inputType, interactionProps, isSelectField, showMode])

    return (
        <SCxContainer style={style}>
            {isSubForm && (
                <>
                    <Breadcrumbs items={[{ icon: <IconFont type="ArrowLeft" />, label: FieldInputNameMap[inputType], onClick: onBack }]} />
                    <Divider />
                </>
            )}
            <BlockConfigureSegmentedControl isInterAction={!isSubForm} value={currentTab} onChange={setCurrentTab} />

            {/* 设计 */}
            <SCxItem key={BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN}>
                {inputType === 'calendar' && (
                    <>
                        <CalendarMode />
                        <Divider />
                    </>
                )}

                {inputType === 'file' && (
                    <>
                        <FileMode />
                        <Divider />
                    </>
                )}

                {isNotSubForm && (
                    <>
                        <PositionSetting {...styleProps.position} />
                        <Divider />
                        <SizeSetting {...styleProps.size} disableHeightAuto={inputType === 'calendar'} />
                    </>
                )}
                <Style prefix={prefix} pointer={pointer} field={field} mode={mode} />

                {/* 断点样式 */}
                {styleProps.font && inputType !== 'calendar' && (
                    <>
                        <Divider />
                        <FontSetting
                            {...styleProps.font}
                            enableFontSize={enableInputFontTextStyle}
                            enableFontWeight={enableInputFontTextStyle}
                        />
                    </>
                )}
                {inputType === 'file' && fileMode === FILE_MODE.grid && (
                    <>
                        <Divider />
                        <FileGridStyle prefix={prefix} />
                    </>
                )}

                {/* 断点样式 */}
                {designProps && (
                    <>
                        <Divider />
                        <Group label="样式">
                            <DesignSetting
                                blockType="field"
                                colorLabel="文本颜色"
                                theme={theme}
                                {...designProps}
                                afterSlot={designAfterSlot}
                            />
                        </Group>
                    </>
                )}

                {inputType === 'checkbox' && (
                    <>
                        <Divider color="var(--color-gray-200)" />
                        <CheckboxConfig prefix={prefix} />
                    </>
                )}
                <Divider color="var(--color-gray-200)" />
                <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
            </SCxItem>

            {/* 配置 */}
            <SCxItem key={BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE}>
                <Data pointer={pointer} prefix={prefix} mode={mode} id={id} fieldOptions={filterFieldOptions} />
                {isNotSubForm && (
                    <>
                        <AnchorSetting />
                        <Divider />
                        <VisibilitySetting />
                    </>
                )}
            </SCxItem>
            {(mode === 'field' || mode === 'form') && (
                <SCxItem key={BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION}>
                    {EVENTS_FIELDS.has(inputType) && (
                        <>
                            <EventsConfigure inputType={inputType} />
                            <Divider />
                        </>
                    )}
                    <AnimationSetting />
                </SCxItem>
            )}
        </SCxContainer>
    )
}
